import { Toasts } from '@fourthwall/components'
import type { ShopsApiClient } from '@fourthwall/services/dist/ShopsApiClient'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'

import type { EnabledCurrency, FormValues } from '../Checkout.types'
import { useUpdatePaymentSettingsMutation } from '../queries/useUpdatePaymentSettingsMutation'
import { useUpdateShopCurrencySettingsMutation } from '../queries/useUpdateShopCurrencySettingsMutation'
import { useAbandonedCheckoutRemindersSettings } from './useAbandonedCheckoutRemindersSettings'
import { useCurrencySettings } from './useCurrencySettings'

type AllowedPaymentProviders = NonNullable<
  Awaited<ReturnType<typeof ShopsApiClient.getCurrentShop>>['data']
>['settings']['payment']['allowedPaymentProviders'][number]

export const useCheckout = () => {
  const { currentShop, invalidateCurrentShopQuery } = useCurrentShop()
  const currentAllowedPaymentProviders = currentShop?.settings.payment.allowedPaymentProviders || []
  const { currencySettings } = useCurrencySettings()
  const { abandonedCheckoutRemindersSettings } = useAbandonedCheckoutRemindersSettings()
  const { updatePaymentSettingsMutation } = useUpdatePaymentSettingsMutation()
  const { updateShopCurrencySettingsMutation } = useUpdateShopCurrencySettingsMutation()

  const getAllowPaymentProviders = (values: FormValues) => {
    return [
      currentAllowedPaymentProviders.includes('Klarna') ? ('Klarna' as const) : undefined,
      currentAllowedPaymentProviders.includes('Afterpay') ? ('Afterpay' as const) : undefined,
      values.payPal ? ('PayPal' as const) : undefined,
      values.deferred ? ('StripeDeferred' as const) : undefined,
    ].filter(Boolean) as AllowedPaymentProviders[]
  }

  const handleSubmit = async (values: FormValues) => {
    if (!currentShop?.id) return

    try {
      const allowedPaymentProviders = getAllowPaymentProviders(values)

      await Promise.all([
        await updatePaymentSettingsMutation.mutateAsync([
          { shopId: currentShop?.id! },
          {
            allowedPaymentProviders,
          },
        ]),
        await updateShopCurrencySettingsMutation.mutateAsync([
          { shopId: currentShop.id },
          {
            defaultCurrency: 'USD',
            enabledCurrencies: values.enabledCurrencies,
          },
        ]),
        await abandonedCheckoutRemindersSettings.update(values),
      ])

      invalidateCurrentShopQuery()

      Toasts.notify('Settings saved', { type: 'info' })
    } catch (error) {
      Toasts.notify('Failed to update shop details. Please try again.')
    }
  }

  const getEnabledCurrencies = (): EnabledCurrency[] => {
    if (!currentShop?.settings.enabledCurrencies) {
      return []
    }

    return currentShop.settings.enabledCurrencies.filter((currency) =>
      currencySettings.list.some((listItem) => listItem.currency === currency),
    ) as EnabledCurrency[]
  }

  return {
    checkout: {
      initialValues: {
        payPal: currentAllowedPaymentProviders.includes('PayPal'),
        deferred: currentAllowedPaymentProviders.includes('StripeDeferred'),
        enabledCurrencies: getEnabledCurrencies(),
        ...abandonedCheckoutRemindersSettings.initialValues,
      },
      abandonedCheckout: abandonedCheckoutRemindersSettings.abandonedCheckout,
      currency: {
        list: currencySettings.list,
        enabled: currencySettings.enabled,
        yenEnabled: currencySettings.yenEnabled,
      },
      onSubmit: handleSubmit,
    },
  }
}
